import React, { useCallback, useEffect, useMemo } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import Button from "@components/Button/Button";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  FottServiceType,
  MatrixManifold,
  PiggingProductType,
  type BerthLocation,
  type TerminalServiceCompany,
  type TerminalServiceCompanyFilterKey,
} from "@services/terminal-services/types";
import type { TerminalServiceDetailsResponse } from "@services/terminal-services/types";
import { getBerthLocations, getTerminalServiceCompanies } from "@services/terminalServicesApi";
import { getVessels } from "@services/vesselsApi";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FOTT_SERVICE_REQUEST_FIELDS as F,
  FOTT_FROM_TO_LOCATION,
  FOTT_SERVICE_REQUEST_FORM_STORAGE_KEY,
  MAX_BYPASS_SHORT_TEXT_LENGTH,
  MAX_BYPASS_TEXT_LENGTH,
  MAX_FOTT_SERVICE_REMARKS_LENGTH,
  MAX_GANTRY_ATTACHMENTS,
  MAX_GANTRY_CRANE_TEXT_LENGTH,
  MAX_NITROGEN_ATTACHMENTS,
  MAX_NITROGEN_SHORT_TEXT_LENGTH,
  MAX_NITROGEN_TEXT_LENGTH,
  MAX_NITROGEN_USAGE_HOURS_DIGITS,
  MAX_OPERATOR_LICENSE_FILES,
  MAX_PIGGING_ATTACHMENTS,
  MAX_PIGGING_SHORT_TEXT_LENGTH,
  MAX_PIGGING_TANK_NO_LENGTH,
  MAX_PIGGING_TEXT_LENGTH,
  OTHER_COMPANY_VALUE,
  yesNoOptions,
} from "./request/constants";
import { useCreateBypassServiceRequest } from "./request/hooks/useCreateBypassServiceRequest";
import { useCreateGantryCraneServiceRequest } from "./request/hooks/useCreateGantryCraneServiceRequest";
import { useCreateNitrogenSupplyServiceRequest } from "./request/hooks/useCreateNitrogenSupplyServiceRequest";
import { useCreatePiggingServiceRequest } from "./request/hooks/useCreatePiggingServiceRequest";
import {
  fottServiceRequestDefaultValues,
  getEndOfMonthDateTime,
  getFottServiceRequestFormSchema,
  type FottServiceRequestFormData,
} from "./request/schema";
import {
  useFottServiceRequestFormStore,
  type FottServiceRequestFormState,
} from "./request/store/useFottServiceRequestForm.store";
import TerminalServiceDocumentsCard from "./TerminalServiceDocumentsCard";

const pickFormValues = (state: FottServiceRequestFormState): FottServiceRequestFormData => {
  return {
    [F.SERVICE_TYPE]: state[F.SERVICE_TYPE],
    [F.BOOKING_FROM]: state[F.BOOKING_FROM],
    [F.BOOKING_TO]: state[F.BOOKING_TO],
    [F.STARTING_FROM]: state[F.STARTING_FROM],
    [F.START_TIME]: state[F.START_TIME],
    [F.USAGE_HOURS]: state[F.USAGE_HOURS],
    [F.MATRIX_MANIFOLD]: state[F.MATRIX_MANIFOLD],
    [F.ON_BEHALF_OF_COMPANY]: state[F.ON_BEHALF_OF_COMPANY],
    [F.ON_BEHALF_OF_COMPANY_OTHER]: state[F.ON_BEHALF_OF_COMPANY_OTHER],
    [F.CHARGEABLE_PARTY]: state[F.CHARGEABLE_PARTY],
    [F.CHARGEABLE_PARTY_OTHER]: state[F.CHARGEABLE_PARTY_OTHER],
    [F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]: state[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF],
    [F.OPERATOR_NEEDED]: state[F.OPERATOR_NEEDED],
    [F.CUSTOMER_JL_SLOT_SIZE]: state[F.CUSTOMER_JL_SLOT_SIZE],
    [F.CUSTOMER_JL_NO]: state[F.CUSTOMER_JL_NO],
    [F.CUSTOMER_TANK_NO]: state[F.CUSTOMER_TANK_NO],
    [F.FOTT_DOCK_LINE_NO_SIZE]: state[F.FOTT_DOCK_LINE_NO_SIZE],
    [F.FOTT_DOCK_LINE]: state[F.FOTT_DOCK_LINE],
    [F.PRODUCT_TYPE]: state[F.PRODUCT_TYPE],
    [F.USAGE_REASON]: state[F.USAGE_REASON],
    [F.REMARKS]: state[F.REMARKS],
    [F.OPERATOR_LICENSE]: state[F.OPERATOR_LICENSE],
    [F.DATE_TIME]: state[F.DATE_TIME],
    [F.LOCATION]: state[F.LOCATION],
    [F.VESSEL_IMO]: state[F.VESSEL_IMO],
    [F.VESSEL_NAME]: state[F.VESSEL_NAME],
    [F.TERMINAL]: state[F.TERMINAL],
    [F.OPERATION_TYPE]: state[F.OPERATION_TYPE],
    [F.DOCK_LINE]: state[F.DOCK_LINE],
    [F.MOV_NUMBER]: state[F.MOV_NUMBER],
    [F.PLTA]: state[F.PLTA],
    [F.PLDA_NUMBER]: state[F.PLDA_NUMBER],
    [F.REASON_FOR_BYPASS]: state[F.REASON_FOR_BYPASS],
    [F.EXPECTED_TIME_OF_CLOSURE]: state[F.EXPECTED_TIME_OF_CLOSURE],
    [F.PHYSICAL_VERIFICATION_OF_VALVE_TAG]: state[F.PHYSICAL_VERIFICATION_OF_VALVE_TAG],
    [F.COLLECTION_OF_VALVE_KEY]: state[F.COLLECTION_OF_VALVE_KEY],
    [F.REMOVAL_OF_PADLOCK]: state[F.REMOVAL_OF_PADLOCK],
    [F.CROSS_CHECK_COMMUNICATION]: state[F.CROSS_CHECK_COMMUNICATION],
    [F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER]: state[F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER],
    [F.CONFIRMATION_OF_INSTRUCTIONS_FOTT]: state[F.CONFIRMATION_OF_INSTRUCTIONS_FOTT],
    [F.ATTACHMENT]: state[F.ATTACHMENT],
  };
};
const toDocumentArray = (documents: any) => {
  if (!documents) {
    return [];
  }

  return Array.isArray(documents) ? documents : [documents];
};
const mapUpdateTerminalServiceDetailsToFottFormValues = (
  terminalServiceDetails: TerminalServiceDetailsResponse,
): FottServiceRequestFormData => {
  const serviceDetails = terminalServiceDetails.serviceDetails as any;
  return {
    [F.SERVICE_TYPE]: terminalServiceDetails.serviceType ?? "",
    [F.BOOKING_FROM]: serviceDetails?.bookingFrom ?? null,
    [F.BOOKING_TO]: serviceDetails?.bookingTo ?? null,
    [F.STARTING_FROM]: serviceDetails?.startingFrom ?? null,
    [F.START_TIME]: serviceDetails?.startTime ?? null,
    [F.USAGE_HOURS]: serviceDetails?.usageHours != null ? String(serviceDetails.usageHours) : "",
    [F.MATRIX_MANIFOLD]: serviceDetails?.matrixManifold ?? "",
    [F.ON_BEHALF_OF_COMPANY]: serviceDetails?.onBehalfCompanyCode ?? "",
    [F.ON_BEHALF_OF_COMPANY_OTHER]: serviceDetails?.onBehalfCompanyOther ?? "",
    [F.CHARGEABLE_PARTY]: serviceDetails?.chargeablePartyCompanyCode ?? "",
    [F.CHARGEABLE_PARTY_OTHER]: serviceDetails?.chargeablePartyOther ?? "",
    [F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]: serviceDetails?.chargeablePartySameAsOnBehalf ?? true,
    [F.OPERATOR_NEEDED]: serviceDetails?.operatorNeeded ?? true,
    [F.CUSTOMER_JL_SLOT_SIZE]: serviceDetails?.customerJlSlotSize ?? "",
    [F.CUSTOMER_JL_NO]: serviceDetails?.customerJlNo ?? "",
    [F.CUSTOMER_TANK_NO]: serviceDetails?.customerTankNo ?? "",
    [F.FOTT_DOCK_LINE_NO_SIZE]: serviceDetails?.fottDockLineNoSize ?? "",
    [F.FOTT_DOCK_LINE]: serviceDetails?.fottDockLine ?? "",
    [F.PRODUCT_TYPE]: serviceDetails?.productType ?? "",
    [F.USAGE_REASON]: serviceDetails?.usageReason ?? "",
    [F.REMARKS]: serviceDetails?.remarks ?? "",
    [F.DATE_TIME]: serviceDetails?.sourceDateTime ?? null,
    [F.LOCATION]: serviceDetails?.location ?? "",
    [F.VESSEL_IMO]: serviceDetails?.vesselImo != null ? String(serviceDetails.vesselImo) : "",
    [F.VESSEL_NAME]: serviceDetails?.vesselName ?? "",
    [F.TERMINAL]: serviceDetails?.customerTerminal ?? "",
    [F.OPERATION_TYPE]: serviceDetails?.operationType ?? "",
    [F.DOCK_LINE]: serviceDetails?.dockLine ?? "",
    [F.MOV_NUMBER]: serviceDetails?.movNumber ?? "",
    [F.PLTA]: serviceDetails?.plta ?? "",
    [F.PLDA_NUMBER]: serviceDetails?.pldaNumber ?? "",
    [F.REASON_FOR_BYPASS]: serviceDetails?.reasonForBypass ?? "",
    [F.EXPECTED_TIME_OF_CLOSURE]: serviceDetails?.expectedClosureDate ?? null,
    [F.PHYSICAL_VERIFICATION_OF_VALVE_TAG]: serviceDetails?.physicalVerificationOfValveTag ?? true,
    [F.COLLECTION_OF_VALVE_KEY]: serviceDetails?.collectionOfValveKeyFromFottCcr ?? true,
    [F.REMOVAL_OF_PADLOCK]: serviceDetails?.removalOfPadlockWitnessedByFottOperator ?? true,
    [F.CROSS_CHECK_COMMUNICATION]: serviceDetails?.crossCheckCommunication ?? true,
    [F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER]:
      serviceDetails?.confirmationOfInstructionsWithCustomerCcr ?? true,
    [F.CONFIRMATION_OF_INSTRUCTIONS_FOTT]:
      serviceDetails?.confirmationOfInstructionsWithFottCcr ?? true,

    [F.OPERATOR_LICENSE]: serviceDetails?.operatorLicenseDocument
      ? [
          {
            name: serviceDetails.operatorLicenseDocument.documentName ?? "",
            documentName:
              serviceDetails.operatorLicenseDocument.documentTitle ??
              serviceDetails.operatorLicenseDocument.documentName ??
              "",
            documentUrl: serviceDetails.operatorLicenseDocument.documentUrl ?? "",
            url: serviceDetails.operatorLicenseDocument.documentUrl,
            documentType: serviceDetails.operatorLicenseDocument.documentType ?? "",
            documentTypeOther:
              serviceDetails.operatorLicenseDocument.otherDocumentType ?? undefined,
            documentTitle: serviceDetails.operatorLicenseDocument.documentTitle ?? "",
            issueDate: serviceDetails.operatorLicenseDocument.issueDate ?? null,
            expirationDate: serviceDetails.operatorLicenseDocument.expirationDate ?? null,
          },
        ]
      : [],

    [F.ATTACHMENT]: toDocumentArray(serviceDetails?.attachments).map((attachment: any) => ({
      name: attachment.documentTitle ?? attachment.documentName ?? "",
      url: attachment.documentUrl,
      documentName: attachment.documentTitle ?? attachment.documentName ?? "",
      documentUrl: attachment.documentUrl ?? "",
      documentType: attachment.documentType ?? "",
      otherDocumentType: attachment.otherDocumentType ?? undefined,
      documentTitle: attachment.documentTitle ?? "",
      issueDate: attachment.issueDate ?? undefined,
      expirationDate: attachment.expirationDate ?? undefined,
      documentSummary: attachment.documentSummary ?? undefined,
    })),
  };
};

const getCompanyFilterKey = (
  serviceType: FottServiceRequestFormData[typeof F.SERVICE_TYPE],
): TerminalServiceCompanyFilterKey | null => {
  switch (serviceType) {
    case FottServiceType.GANTRY_CRANE_SERVICE:
    case FottServiceType.NITROGEN_SUPPLY_SERVICE:
    case FottServiceType.PIGGING_SERVICE:
      return "isVendor";
    case FottServiceType.BYPASS_SERVICE:
      return "isOilCompany";
    default:
      return null;
  }
};

const getMatchingVessels = async (imoNumber: string) => {
  const res = await getVessels("bff-vessel", {
    imoNumber,
    // two since one can be the current vessel being edited and we ignore that in the check,
    // so the other one would be the duplicate if it exists
    pageSize: 2,
    pageNumber: 1,
  });

  return res;
};

const FottServiceRequestContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const location = useLocation();
  const locationState = location.state as
    | {
        terminalServiceDetails: TerminalServiceDetailsResponse;
        mode: "update";
      }
    | undefined;

  const updateTerminalServiceDetails = locationState?.terminalServiceDetails;
  const isUpdateMode = locationState?.mode === "update";

  const updateTerminalServiceId =
    updateTerminalServiceDetails?.id ??
    (updateTerminalServiceDetails?.serviceDetails as { id?: string } | null | undefined)?.id;

  const navigate = useNavigate();
  const { user, userActionPermissions } = useAuthStore();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();
  const [companies, setCompanies] = React.useState<TerminalServiceCompany[]>([]);
  const [berthLocations, setBerthLocations] = React.useState<BerthLocation[]>([]);
  const companyCacheRef = React.useRef<
    Partial<Record<TerminalServiceCompanyFilterKey, TerminalServiceCompany[]>>
  >({});
  const companyRequestsRef = React.useRef<
    Partial<Record<TerminalServiceCompanyFilterKey, Promise<TerminalServiceCompany[]>>>
  >({});
  const berthLocationsCacheRef = React.useRef<BerthLocation[] | null>(null);
  const berthLocationsRequestRef = React.useRef<Promise<BerthLocation[]> | null>(null);
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const isCustomer = user?.roles.includes("customer") ?? false;
  const canRequestFottService =
    isCustomer && checkPermission(userActionPermissions, "canRequestFottService");

  useEffect(() => {
    if (!canRequestFottService) {
      navigate(`/${paths.terminalServices.index}/${paths.terminalServices.fottServices}`, {
        replace: true,
      });
    }
  }, [canRequestFottService, navigate]);

  const schema = useMemo(() => getFottServiceRequestFormSchema(t), [t]);
  const { setField, setAll, resetForm } = useFottServiceRequestFormStore();
  const initialStoreValues = pickFormValues(useFottServiceRequestFormStore.getState());
  const form = usePersistentForm<FottServiceRequestFormData>({
    schema,
    defaultValues: fottServiceRequestDefaultValues,
    storeValues: initialStoreValues,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: FOTT_SERVICE_REQUEST_FORM_STORAGE_KEY,
    dateKeys: [
      F.BOOKING_FROM,
      F.BOOKING_TO,
      F.STARTING_FROM,
      F.START_TIME,
      F.DATE_TIME,
      F.EXPECTED_TIME_OF_CLOSURE,
    ],
  });

  const {
    clear,
    clearErrors,
    formState: { errors: formErrors },
    getValues,
    reset,
    setError,
    setValue,
    watch,
  } = form;
  const selectedServiceType = watch(F.SERVICE_TYPE);
  const companyFilterKey = useMemo(
    () => getCompanyFilterKey(selectedServiceType),
    [selectedServiceType],
  );
  const selectedOnBehalfCompany = watch(F.ON_BEHALF_OF_COMPANY);
  const selectedOnBehalfCompanyOther = watch(F.ON_BEHALF_OF_COMPANY_OTHER);
  const isChargeablePartySame = watch(F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF);
  const bookingFrom = watch(F.BOOKING_FROM);
  const bookingToMaxDate = useMemo(() => getEndOfMonthDateTime(bookingFrom), [bookingFrom]);
  const vesselIMO = watch(F.VESSEL_IMO);
  const vesselIMOError = formErrors[F.VESSEL_IMO];
  const [errors, setErrors] = React.useState<{
    vesselIMO?: string;
  }>({});

  const setVesselImoNotFoundError = useCallback(
    (invalidIMO: string) => {
      setErrors((currentErrors) => ({
        ...currentErrors,
        vesselIMO: invalidIMO,
      }));
      setError(F.VESSEL_IMO, {
        type: "manual",
        message: t("form-validation.imo-does-not-exist", {
          label: t("terminal-services.fott-service-request.fields.vessel-imo"),
        }),
      });
    },
    [setError, t],
  );

  const clearVesselImoNotFoundError = useCallback(() => {
    setErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors.vesselIMO;
      return nextErrors;
    });
    clearErrors(F.VESSEL_IMO);
  }, [clearErrors]);

  useEffect(() => {
    if (errors.vesselIMO && errors.vesselIMO === vesselIMO && !vesselIMOError) {
      setVesselImoNotFoundError(errors.vesselIMO);
    }
  }, [errors.vesselIMO, setVesselImoNotFoundError, vesselIMO, vesselIMOError]);

  useEffect(() => {
    let isActive = true;

    if (!companyFilterKey) {
      setCompanies([]);
      return () => {
        isActive = false;
      };
    }

    const cachedCompanies = companyCacheRef.current[companyFilterKey];

    if (cachedCompanies) {
      setCompanies(cachedCompanies);
      return () => {
        isActive = false;
      };
    }

    setCompanies([]);

    let companiesRequest = companyRequestsRef.current[companyFilterKey];

    if (!companiesRequest) {
      companiesRequest = getTerminalServiceCompanies(companyFilterKey)
        .then((loadedCompanies) => {
          companyCacheRef.current[companyFilterKey] = loadedCompanies;
          return loadedCompanies;
        })
        .finally(() => {
          delete companyRequestsRef.current[companyFilterKey];
        });
      companyRequestsRef.current[companyFilterKey] = companiesRequest;
    }

    companiesRequest
      .then((loadedCompanies) => {
        if (isActive) {
          setCompanies(loadedCompanies);
        }
      })
      .catch((error: unknown) => {
        if (isActive) {
          setCompanies([]);
        }
        console.error("Load terminal service companies failed:", error);
      });

    return () => {
      isActive = false;
    };
  }, [companyFilterKey]);

  useEffect(() => {
    if (selectedServiceType !== FottServiceType.BYPASS_SERVICE) {
      setBerthLocations([]);
      return;
    }

    let isActive = true;

    if (berthLocationsCacheRef.current) {
      setBerthLocations(berthLocationsCacheRef.current);
      return () => {
        isActive = false;
      };
    }

    setBerthLocations([]);

    if (!berthLocationsRequestRef.current) {
      berthLocationsRequestRef.current = getBerthLocations({
        fromToLocation: FOTT_FROM_TO_LOCATION,
      })
        .then((loadedBerthLocations) => {
          berthLocationsCacheRef.current = loadedBerthLocations;
          return loadedBerthLocations;
        })
        .finally(() => {
          berthLocationsRequestRef.current = null;
        });
    }

    berthLocationsRequestRef.current
      .then((loadedBerthLocations) => {
        if (isActive) {
          setBerthLocations(loadedBerthLocations);
        }
      })
      .catch((error: unknown) => {
        if (isActive) {
          setBerthLocations([]);
        }
        console.error("Load FOTT berth locations failed:", error);
      });

    return () => {
      isActive = false;
    };
  }, [selectedServiceType]);

  useEffect(() => {
    let isActive = true;

    const setVesselNameValue = (vesselName: string) => {
      setValue(F.VESSEL_NAME, vesselName, {
        shouldDirty: false,
        shouldValidate: false,
      });

      setField(F.VESSEL_NAME, vesselName);
    };

    const clearVesselName = () => {
      if (getValues(F.VESSEL_NAME)) {
        setVesselNameValue("");
      }
    };

    const loadMatchingVessel = async () => {
      if (selectedServiceType !== FottServiceType.BYPASS_SERVICE || !vesselIMO) {
        clearVesselName();
        clearVesselImoNotFoundError();
        return;
      }

      clearVesselImoNotFoundError();
      clearVesselName();

      try {
        const matchingVessels = await getMatchingVessels(vesselIMO);

        if (!isActive) {
          return;
        }

        const vesselName =
          matchingVessels.totalRecordCount === 1 ? matchingVessels.vessels[0]?.vesselName : "";

        setVesselNameValue(vesselName);

        if (!vesselName) {
          setVesselImoNotFoundError(vesselIMO);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error("Load matching vessel failed:", error);
        clearVesselName();
        setVesselImoNotFoundError(vesselIMO);
      }
    };

    void loadMatchingVessel();

    return () => {
      isActive = false;
    };
  }, [
    clearVesselImoNotFoundError,
    getValues,
    selectedServiceType,
    setField,
    setValue,
    setVesselImoNotFoundError,
    vesselIMO,
  ]);

  const serviceTypeOptions = useMemo(
    () => [
      {
        value: FottServiceType.GANTRY_CRANE_SERVICE,
        label: t("terminal-services.fott-service-request.service-types.gantry-crane"),
      },
      {
        value: FottServiceType.NITROGEN_SUPPLY_SERVICE,
        label: t("terminal-services.fott-service-request.service-types.nitrogen-supply"),
      },
      {
        value: FottServiceType.PIGGING_SERVICE,
        label: t("terminal-services.fott-service-request.service-types.pigging"),
      },
      {
        value: FottServiceType.BYPASS_SERVICE,
        label: t("terminal-services.fott-service-request.service-types.bypass"),
      },
    ],
    [t],
  );

  const matrixManifoldOptions = useMemo(
    () => [
      { value: MatrixManifold.MM1_EAST, label: "MM1 East" },
      { value: MatrixManifold.MM1_WEST, label: "MM1 West" },
      { value: MatrixManifold.MM2_NORTH, label: "MM2 North" },
      { value: MatrixManifold.MM2_SOUTH, label: "MM2 South" },
    ],
    [],
  );

  const companyOptions = useMemo(
    () => [
      ...companies.map(({ companyCode, companyNameEnglish }) => ({
        value: companyCode,
        label: companyNameEnglish,
      })),
      {
        value: OTHER_COMPANY_VALUE,
        label: t("terminal-services.fott-service-request.fields.other"),
      },
    ],
    [companies, t],
  );

  const berthLocationOptions = useMemo(
    () =>
      berthLocations.map(({ berthCode, berthName }) => ({
        value: berthCode,
        label: berthName,
      })),
    [berthLocations],
  );

  const piggingProductTypeOptions = useMemo(
    () => [
      {
        value: PiggingProductType.BLACK,
        label: t("terminal-services.fott-service-request.product-types.black"),
      },
      {
        value: PiggingProductType.WHITE,
        label: t("terminal-services.fott-service-request.product-types.white"),
      },
    ],
    [t],
  );

  const allowedOperatorLicenseExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const {
    operatorLicenseFiles,
    setOperatorLicenseFiles,
    handleOperatorLicenseUpload,
    removeOperatorLicense,
    resetOperatorLicenseUpload,
    isUploadingOperatorLicense,
    isSubmitting: isSubmittingGantryCrane,
    submitRequest: submitGantryCraneRequest,
    resubmitRequest: resubmitGantryCraneRequest,
  } = useCreateGantryCraneServiceRequest();
  const {
    attachmentFiles,
    setAttachmentFiles,
    handleAttachmentUpload,
    resetAttachmentUpload,
    isUploadingAttachment,
    isSubmitting: isSubmittingNitrogenSupply,
    submitRequest: submitNitrogenSupplyRequest,
    resubmitRequest: resubmitNitrogenSupplyRequest,
  } = useCreateNitrogenSupplyServiceRequest();
  const {
    attachmentFiles: piggingAttachmentFiles,
    setAttachmentFiles: setPiggingAttachmentFiles,
    handleAttachmentUpload: handlePiggingAttachmentUpload,
    resetAttachmentUpload: resetPiggingAttachmentUpload,
    isUploadingAttachment: isUploadingPiggingAttachment,
    isSubmitting: isSubmittingPigging,
    submitRequest: submitPiggingRequest,
    resubmitRequest: resubmitPiggingRequest,
  } = useCreatePiggingServiceRequest();
  const {
    attachmentFiles: bypassAttachmentFiles,
    setAttachmentFiles: setBypassAttachmentFiles,
    handleAttachmentUpload: handleBypassAttachmentUpload,
    resetAttachmentUpload: resetBypassAttachmentUpload,
    isUploadingAttachment: isUploadingBypassAttachment,
    isSubmitting: isSubmittingBypass,
    submitRequest: submitBypassRequest,
    resubmitRequest: resubmitBypassRequest,
  } = useCreateBypassServiceRequest();

  const shouldShowGantryCraneFields = selectedServiceType === FottServiceType.GANTRY_CRANE_SERVICE;
  const shouldShowNitrogenSupplyFields =
    selectedServiceType === FottServiceType.NITROGEN_SUPPLY_SERVICE;
  const shouldShowPiggingFields = selectedServiceType === FottServiceType.PIGGING_SERVICE;
  const shouldShowBypassFields = selectedServiceType === FottServiceType.BYPASS_SERVICE;
  const shouldShowAttachmentSection =
    shouldShowGantryCraneFields ||
    shouldShowNitrogenSupplyFields ||
    shouldShowPiggingFields ||
    shouldShowBypassFields;
  const isSubmitting =
    isSubmittingGantryCrane ||
    isSubmittingNitrogenSupply ||
    isSubmittingPigging ||
    isSubmittingBypass;
  const isUploading =
    isUploadingOperatorLicense ||
    isUploadingAttachment ||
    isUploadingPiggingAttachment ||
    isUploadingBypassAttachment;

  const goToFottServicesList = useCallback(() => {
    navigate(`/${paths.terminalServices.index}/${paths.terminalServices.fottServices}`);
  }, [navigate]);

  const clearRequestForm = useCallback(() => {
    clear();
    localStorage.removeItem(FOTT_SERVICE_REQUEST_FORM_STORAGE_KEY);
    resetOperatorLicenseUpload();
    resetAttachmentUpload();
    resetPiggingAttachmentUpload();
    resetBypassAttachmentUpload();
  }, [
    clear,
    resetAttachmentUpload,
    resetOperatorLicenseUpload,
    resetPiggingAttachmentUpload,
    resetBypassAttachmentUpload,
  ]);

  const updateFormData = useCallback(
    <K extends keyof FottServiceRequestFormData>(key: K, value: FottServiceRequestFormData[K]) => {
      if (isUpdateMode) {
        return;
      }

      setField(key, value);
    },
    [isUpdateMode, setField],
  );

  const handleServiceTypeChange = useCallback(
    (_key: typeof F.SERVICE_TYPE, value: FottServiceRequestFormData[typeof F.SERVICE_TYPE]) => {
      const nextValues: FottServiceRequestFormData = {
        ...fottServiceRequestDefaultValues,
        [F.SERVICE_TYPE]: value,
      };

      reset(nextValues);
      resetForm();
      localStorage.removeItem(FOTT_SERVICE_REQUEST_FORM_STORAGE_KEY);
      setAll(nextValues);
      resetOperatorLicenseUpload();
      resetAttachmentUpload();
      resetPiggingAttachmentUpload();
      resetBypassAttachmentUpload();
      clearErrors();
    },
    [
      clearErrors,
      reset,
      resetAttachmentUpload,
      resetForm,
      resetOperatorLicenseUpload,
      resetPiggingAttachmentUpload,
      resetBypassAttachmentUpload,
      setAll,
    ],
  );

  useEffect(() => {
    if (!isUpdateMode || !updateTerminalServiceDetails) {
      return;
    }

    const formValues = mapUpdateTerminalServiceDetailsToFottFormValues(
      updateTerminalServiceDetails,
    );

    reset(formValues);
    setOperatorLicenseFiles(
      formValues[F.OPERATOR_LICENSE] as Parameters<typeof setOperatorLicenseFiles>[0],
    );
    if (formValues[F.SERVICE_TYPE] === FottServiceType.GANTRY_CRANE_SERVICE) {
      setAttachmentFiles(formValues[F.ATTACHMENT] as Parameters<typeof setAttachmentFiles>[0]);
    }
    if (formValues[F.SERVICE_TYPE] === FottServiceType.NITROGEN_SUPPLY_SERVICE) {
      setAttachmentFiles(formValues[F.ATTACHMENT] as Parameters<typeof setAttachmentFiles>[0]);
    }

    if (formValues[F.SERVICE_TYPE] === FottServiceType.PIGGING_SERVICE) {
      setPiggingAttachmentFiles(
        formValues[F.ATTACHMENT] as Parameters<typeof setPiggingAttachmentFiles>[0],
      );
    }

    if (formValues[F.SERVICE_TYPE] === FottServiceType.BYPASS_SERVICE) {
      setBypassAttachmentFiles(
        formValues[F.ATTACHMENT] as Parameters<typeof setBypassAttachmentFiles>[0],
      );
    }
  }, [
    isUpdateMode,
    reset,
    setAttachmentFiles,
    setBypassAttachmentFiles,
    setOperatorLicenseFiles,
    setPiggingAttachmentFiles,
    updateTerminalServiceDetails,
  ]);

  useEffect(() => {
    if (
      !selectedServiceType ||
      selectedServiceType === FottServiceType.GANTRY_CRANE_SERVICE ||
      selectedServiceType === FottServiceType.NITROGEN_SUPPLY_SERVICE ||
      selectedServiceType === FottServiceType.PIGGING_SERVICE
    ) {
      return;
    }
    if (isUpdateMode) {
      return;
    }
    setValue(F.BOOKING_FROM, null, { shouldDirty: false, shouldValidate: false });
    setValue(F.BOOKING_TO, null, { shouldDirty: false, shouldValidate: false });
    setValue(F.STARTING_FROM, null, { shouldDirty: false, shouldValidate: false });
    setValue(F.START_TIME, null, { shouldDirty: false, shouldValidate: false });
    setValue(F.USAGE_HOURS, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.MATRIX_MANIFOLD, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.ON_BEHALF_OF_COMPANY, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.ON_BEHALF_OF_COMPANY_OTHER, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.CHARGEABLE_PARTY, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.CHARGEABLE_PARTY_OTHER, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF, true, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue(F.OPERATOR_NEEDED, true, { shouldDirty: false, shouldValidate: false });
    setValue(F.CUSTOMER_JL_SLOT_SIZE, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.CUSTOMER_JL_NO, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.CUSTOMER_TANK_NO, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.FOTT_DOCK_LINE_NO_SIZE, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.FOTT_DOCK_LINE, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.PRODUCT_TYPE, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.USAGE_REASON, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.REMARKS, "", { shouldDirty: false, shouldValidate: false });
    setValue(F.OPERATOR_LICENSE, [], { shouldDirty: false, shouldValidate: false });
    setValue(F.ATTACHMENT, [], { shouldDirty: false, shouldValidate: false });
    resetOperatorLicenseUpload();
    resetAttachmentUpload();
    resetPiggingAttachmentUpload();
    resetBypassAttachmentUpload();
    clearErrors();
  }, [
    clearErrors,
    isUpdateMode,
    resetAttachmentUpload,
    resetOperatorLicenseUpload,
    resetPiggingAttachmentUpload,
    resetBypassAttachmentUpload,
    selectedServiceType,
    setValue,
  ]);

  useEffect(() => {
    const shouldSyncChargeableParty =
      selectedServiceType === FottServiceType.GANTRY_CRANE_SERVICE ||
      selectedServiceType === FottServiceType.NITROGEN_SUPPLY_SERVICE ||
      selectedServiceType === FottServiceType.PIGGING_SERVICE;

    if (!isChargeablePartySame || !shouldSyncChargeableParty) {
      return;
    }

    setValue(F.CHARGEABLE_PARTY, selectedOnBehalfCompany, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue(F.CHARGEABLE_PARTY_OTHER, selectedOnBehalfCompanyOther, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setField(F.CHARGEABLE_PARTY, selectedOnBehalfCompany);
    setField(F.CHARGEABLE_PARTY_OTHER, selectedOnBehalfCompanyOther);
  }, [
    isChargeablePartySame,
    selectedOnBehalfCompany,
    selectedOnBehalfCompanyOther,
    selectedServiceType,
    setField,
    setValue,
  ]);

  const handleCancel = () => {
    clearRequestForm();
    goToFottServicesList();
  };

  const onInvalid = useCallback(() => {
    setAll(getValues());
    notify({
      title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
      description: t(
        "terminal-services.fott-service-request.notifications.submit-error-description",
      ),
      type: "error",
    });
  }, [getValues, notify, setAll, t]);

  const onSubmit = useCallback(async () => {
    const latestFormValues = getValues();

    if (
      latestFormValues[F.SERVICE_TYPE] === FottServiceType.BYPASS_SERVICE &&
      latestFormValues[F.VESSEL_IMO] &&
      !latestFormValues[F.VESSEL_NAME]
    ) {
      setVesselImoNotFoundError(latestFormValues[F.VESSEL_IMO]);
      onInvalid();
      return;
    }

    setAll(latestFormValues);
    let wasSubmitted = false;

    switch (latestFormValues[F.SERVICE_TYPE]) {
      case FottServiceType.NITROGEN_SUPPLY_SERVICE:
        wasSubmitted = isUpdateMode
          ? await resubmitNitrogenSupplyRequest(updateTerminalServiceId!, latestFormValues)
          : await submitNitrogenSupplyRequest(latestFormValues);
        break;

      case FottServiceType.PIGGING_SERVICE:
        wasSubmitted = isUpdateMode
          ? await resubmitPiggingRequest(updateTerminalServiceId!, latestFormValues)
          : await submitPiggingRequest(latestFormValues);
        break;

      case FottServiceType.BYPASS_SERVICE:
        wasSubmitted = isUpdateMode
          ? await resubmitBypassRequest(updateTerminalServiceId!, latestFormValues)
          : await submitBypassRequest(latestFormValues);
        break;

      default:
        wasSubmitted = isUpdateMode
          ? await resubmitGantryCraneRequest(updateTerminalServiceId!, latestFormValues)
          : await submitGantryCraneRequest(latestFormValues);
    }

    if (!wasSubmitted) {
      return;
    }

    clearRequestForm();
    goToFottServicesList();
  }, [
    clearRequestForm,
    getValues,
    goToFottServicesList,
    isUpdateMode,
    notify,
    onInvalid,
    resubmitBypassRequest,
    resubmitGantryCraneRequest,
    resubmitNitrogenSupplyRequest,
    resubmitPiggingRequest,
    setAll,
    setVesselImoNotFoundError,
    submitBypassRequest,
    submitGantryCraneRequest,
    submitNitrogenSupplyRequest,
    submitPiggingRequest,
    t,
    updateTerminalServiceId,
  ]);

  if (!canRequestFottService) {
    return null;
  }
  const needsCompanyOptions =
    selectedServiceType === FottServiceType.GANTRY_CRANE_SERVICE ||
    selectedServiceType === FottServiceType.NITROGEN_SUPPLY_SERVICE ||
    selectedServiceType === FottServiceType.PIGGING_SERVICE ||
    selectedServiceType === FottServiceType.BYPASS_SERVICE;

  const needsBerthLocationOptions = selectedServiceType === FottServiceType.BYPASS_SERVICE;

  const isWaitingForSelectOptions =
    isUpdateMode &&
    ((needsCompanyOptions && companies.length === 0) ||
      (needsBerthLocationOptions && berthLocations.length === 0));

  if (isWaitingForSelectOptions) {
    return null;
  }

  const renderRemarksField = () => (
    <div className="md:col-span-full">
      <FormItem<FottServiceRequestFormData, typeof F.REMARKS>
        name={F.REMARKS}
        type="textarea"
        minRows={3}
        maxChars={MAX_FOTT_SERVICE_REMARKS_LENGTH}
        showCharCounter
        label={t("terminal-services.fott-service-request.fields.remarks")}
        testId="fott-service-request-remarks"
        onFieldChange={updateFormData}
      />
    </div>
  );

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId="fott-service-request-page-subheader"
        subtitle={t("terminal-services.fott-services.title")}
        title={t("terminal-services.fott-service-request.title")}
      />

      <div className="px-24 py-8">
        <div className="mx-auto max-w-[930px] flex flex-col gap-6">
          <FormProvider {...form}>
            <form
              data-testid="fott-service-request-page"
              onSubmit={(event) => {
                const submitter = (event.nativeEvent as SubmitEvent)
                  .submitter as HTMLElement | null;

                if (submitter?.getAttribute("data-testid")?.endsWith("-delete")) {
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }

                void form.handleSubmit(onSubmit, onInvalid)(event);
              }}
              className="flex flex-col gap-6"
            >
              <FormSection
                title={t("terminal-services.fott-service-request.form-section-title")}
                className={shouldShowPiggingFields ? "grid-cols-1 md:grid-cols-6" : undefined}
              >
                <div className={shouldShowPiggingFields ? "md:col-span-3" : undefined}>
                  <FormItem<FottServiceRequestFormData, typeof F.SERVICE_TYPE>
                    required
                    name={F.SERVICE_TYPE}
                    type="select"
                    options={serviceTypeOptions}
                    label={t("terminal-services.fott-service-request.fields.service-type")}
                    testId="fott-service-request-service-type"
                    onFieldChange={handleServiceTypeChange}
                    disabled={isUpdateMode}
                  />
                </div>
                <div className={shouldShowPiggingFields ? "hidden md:block md:col-span-3" : ""} />

                {shouldShowGantryCraneFields && (
                  <>
                    <FormItem<FottServiceRequestFormData, typeof F.BOOKING_FROM>
                      required
                      name={F.BOOKING_FROM}
                      type="date-picker"
                      combinedInput
                      dateConstraint="not-past"
                      minDate={new Date()}
                      label={t("terminal-services.fott-service-request.fields.booking-from")}
                      testId="fott-service-request-booking-from"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.BOOKING_TO>
                      required
                      name={F.BOOKING_TO}
                      type="date-picker"
                      combinedInput
                      minDate={bookingFrom instanceof Date ? bookingFrom : new Date()}
                      maxDate={bookingToMaxDate}
                      label={t("terminal-services.fott-service-request.fields.booking-to")}
                      testId="fott-service-request-booking-to"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY>
                      required
                      name={F.ON_BEHALF_OF_COMPANY}
                      type="select"
                      options={companyOptions}
                      label={t(
                        "terminal-services.fott-service-request.fields.on-behalf-of-company",
                      )}
                      testId="fott-service-request-on-behalf-of-company"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY>
                      required
                      name={F.CHARGEABLE_PARTY}
                      type="select"
                      options={companyOptions}
                      label={t("terminal-services.fott-service-request.fields.chargeable-party")}
                      testId="fott-service-request-chargeable-party"
                      disabled={isChargeablePartySame}
                      onFieldChange={updateFormData}
                    />
                    {selectedOnBehalfCompany === OTHER_COMPANY_VALUE && (
                      <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY_OTHER>
                        required
                        name={F.ON_BEHALF_OF_COMPANY_OTHER}
                        type="text"
                        maxChars={MAX_GANTRY_CRANE_TEXT_LENGTH}
                        label={t(
                          "terminal-services.fott-service-request.fields.on-behalf-of-company-other",
                        )}
                        testId="fott-service-request-on-behalf-of-company-other"
                        onFieldChange={updateFormData}
                      />
                    )}
                    {!isChargeablePartySame &&
                      watch(F.CHARGEABLE_PARTY) === OTHER_COMPANY_VALUE && (
                        <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY_OTHER>
                          required
                          name={F.CHARGEABLE_PARTY_OTHER}
                          type="text"
                          maxChars={MAX_GANTRY_CRANE_TEXT_LENGTH}
                          label={t(
                            "terminal-services.fott-service-request.fields.chargeable-party-other",
                          )}
                          testId="fott-service-request-chargeable-party-other"
                          onFieldChange={updateFormData}
                        />
                      )}
                    <div className="md:col-span-full -my-2">
                      <FormItem<
                        FottServiceRequestFormData,
                        typeof F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF
                      >
                        name={F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF}
                        type="checkbox"
                        label={t(
                          "terminal-services.fott-service-request.fields.chargeable-party-same-as-on-behalf",
                        )}
                        testId="fott-service-request-chargeable-party-same-as-on-behalf"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <FormItem<FottServiceRequestFormData, typeof F.MATRIX_MANIFOLD>
                      required
                      name={F.MATRIX_MANIFOLD}
                      type="select"
                      options={matrixManifoldOptions}
                      label={t("terminal-services.fott-service-request.fields.matrix-manifold")}
                      testId="fott-service-request-matrix-manifold"
                      onFieldChange={updateFormData}
                    />
                    <div />
                    <div className="md:col-span-full">
                      <FormItem<FottServiceRequestFormData, typeof F.OPERATOR_NEEDED>
                        required
                        name={F.OPERATOR_NEEDED}
                        type="radio"
                        horizontalRadioButtons
                        label={t("terminal-services.fott-service-request.fields.operator-needed")}
                        options={yesNoOptions}
                        testId="fott-service-request-operator-needed"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-full">
                      <FormItem<FottServiceRequestFormData, typeof F.USAGE_REASON>
                        required
                        name={F.USAGE_REASON}
                        type="textarea"
                        minRows={1}
                        maxChars={MAX_GANTRY_CRANE_TEXT_LENGTH}
                        showCharCounter
                        label={t("terminal-services.fott-service-request.fields.usage-reason")}
                        testId="fott-service-request-usage-reason"
                        onFieldChange={updateFormData}
                      />
                    </div>
                  </>
                )}

                {shouldShowNitrogenSupplyFields && (
                  <>
                    <FormItem<FottServiceRequestFormData, typeof F.START_TIME>
                      required
                      name={F.START_TIME}
                      type="date-picker"
                      combinedInput
                      dateConstraint="not-past"
                      minDate={new Date()}
                      label={t("terminal-services.fott-service-request.fields.start-time")}
                      testId="fott-service-request-start-time"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.USAGE_HOURS>
                      name={F.USAGE_HOURS}
                      type="number"
                      maxDigits={MAX_NITROGEN_USAGE_HOURS_DIGITS}
                      label={t("terminal-services.fott-service-request.fields.usage-hours")}
                      testId="fott-service-request-usage-hours"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY>
                      required
                      name={F.ON_BEHALF_OF_COMPANY}
                      type="select"
                      options={companyOptions}
                      label={t(
                        "terminal-services.fott-service-request.fields.on-behalf-of-company",
                      )}
                      testId="fott-service-request-on-behalf-of-company"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY>
                      required
                      name={F.CHARGEABLE_PARTY}
                      type="select"
                      options={companyOptions}
                      label={t("terminal-services.fott-service-request.fields.chargeable-party")}
                      testId="fott-service-request-chargeable-party"
                      disabled={isChargeablePartySame}
                      onFieldChange={updateFormData}
                    />
                    {selectedOnBehalfCompany === OTHER_COMPANY_VALUE && (
                      <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY_OTHER>
                        required
                        name={F.ON_BEHALF_OF_COMPANY_OTHER}
                        type="text"
                        maxChars={MAX_NITROGEN_TEXT_LENGTH}
                        label={t(
                          "terminal-services.fott-service-request.fields.on-behalf-of-company-other",
                        )}
                        testId="fott-service-request-on-behalf-of-company-other"
                        onFieldChange={updateFormData}
                      />
                    )}
                    {!isChargeablePartySame &&
                      watch(F.CHARGEABLE_PARTY) === OTHER_COMPANY_VALUE && (
                        <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY_OTHER>
                          required
                          name={F.CHARGEABLE_PARTY_OTHER}
                          type="text"
                          maxChars={MAX_NITROGEN_TEXT_LENGTH}
                          label={t(
                            "terminal-services.fott-service-request.fields.chargeable-party-other",
                          )}
                          testId="fott-service-request-chargeable-party-other"
                          onFieldChange={updateFormData}
                        />
                      )}
                    <div className="md:col-span-full -my-2">
                      <FormItem<
                        FottServiceRequestFormData,
                        typeof F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF
                      >
                        name={F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF}
                        type="checkbox"
                        label={t(
                          "terminal-services.fott-service-request.fields.chargeable-party-same-as-on-behalf",
                        )}
                        testId="fott-service-request-chargeable-party-same-as-on-behalf"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <FormItem<FottServiceRequestFormData, typeof F.CUSTOMER_JL_SLOT_SIZE>
                      required
                      name={F.CUSTOMER_JL_SLOT_SIZE}
                      type="text"
                      maxChars={MAX_NITROGEN_SHORT_TEXT_LENGTH}
                      label={t(
                        "terminal-services.fott-service-request.fields.customer-jl-slot-size",
                      )}
                      testId="fott-service-request-customer-jl-slot-size"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.FOTT_DOCK_LINE_NO_SIZE>
                      required
                      name={F.FOTT_DOCK_LINE_NO_SIZE}
                      type="textarea"
                      minRows={1}
                      maxChars={MAX_NITROGEN_SHORT_TEXT_LENGTH}
                      label={t(
                        "terminal-services.fott-service-request.fields.fott-dock-line-no-size",
                      )}
                      testId="fott-service-request-fott-dock-line-no-size"
                      onFieldChange={updateFormData}
                    />
                    <div className="md:col-span-full">
                      <FormItem<FottServiceRequestFormData, typeof F.USAGE_REASON>
                        name={F.USAGE_REASON}
                        type="textarea"
                        minRows={1}
                        maxChars={MAX_NITROGEN_TEXT_LENGTH}
                        showCharCounter
                        label={t("terminal-services.fott-service-request.fields.usage-reason")}
                        testId="fott-service-request-usage-reason"
                        onFieldChange={updateFormData}
                      />
                    </div>
                  </>
                )}

                {shouldShowPiggingFields && (
                  <>
                    <div className="md:col-span-3">
                      <FormItem<FottServiceRequestFormData, typeof F.STARTING_FROM>
                        required
                        name={F.STARTING_FROM}
                        type="date-picker"
                        combinedInput
                        dateConstraint="not-past"
                        minDate={new Date()}
                        label={t("terminal-services.fott-service-request.fields.starting-from")}
                        testId="fott-service-request-starting-from"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="hidden md:block md:col-span-3" />
                    <div className="md:col-span-3">
                      <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY>
                        required
                        name={F.ON_BEHALF_OF_COMPANY}
                        type="select"
                        options={companyOptions}
                        label={t(
                          "terminal-services.fott-service-request.fields.on-behalf-of-company",
                        )}
                        testId="fott-service-request-on-behalf-of-company"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY>
                        required
                        name={F.CHARGEABLE_PARTY}
                        type="select"
                        options={companyOptions}
                        label={t("terminal-services.fott-service-request.fields.chargeable-party")}
                        testId="fott-service-request-chargeable-party"
                        disabled={isChargeablePartySame}
                        onFieldChange={updateFormData}
                      />
                    </div>
                    {selectedOnBehalfCompany === OTHER_COMPANY_VALUE && (
                      <div className="md:col-span-3">
                        <FormItem<FottServiceRequestFormData, typeof F.ON_BEHALF_OF_COMPANY_OTHER>
                          required
                          name={F.ON_BEHALF_OF_COMPANY_OTHER}
                          type="text"
                          maxChars={MAX_PIGGING_TEXT_LENGTH}
                          label={t(
                            "terminal-services.fott-service-request.fields.on-behalf-of-company-other",
                          )}
                          testId="fott-service-request-on-behalf-of-company-other"
                          onFieldChange={updateFormData}
                        />
                      </div>
                    )}
                    {!isChargeablePartySame &&
                      watch(F.CHARGEABLE_PARTY) === OTHER_COMPANY_VALUE && (
                        <div className="md:col-span-3">
                          <FormItem<FottServiceRequestFormData, typeof F.CHARGEABLE_PARTY_OTHER>
                            required
                            name={F.CHARGEABLE_PARTY_OTHER}
                            type="text"
                            maxChars={MAX_PIGGING_TEXT_LENGTH}
                            label={t(
                              "terminal-services.fott-service-request.fields.chargeable-party-other",
                            )}
                            testId="fott-service-request-chargeable-party-other"
                            onFieldChange={updateFormData}
                          />
                        </div>
                      )}
                    <div className="md:col-span-full -my-2">
                      <FormItem<
                        FottServiceRequestFormData,
                        typeof F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF
                      >
                        name={F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF}
                        type="checkbox"
                        label={t(
                          "terminal-services.fott-service-request.fields.chargeable-party-same-as-on-behalf",
                        )}
                        testId="fott-service-request-chargeable-party-same-as-on-behalf"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FormItem<FottServiceRequestFormData, typeof F.CUSTOMER_JL_NO>
                        required
                        name={F.CUSTOMER_JL_NO}
                        type="text"
                        maxChars={MAX_PIGGING_SHORT_TEXT_LENGTH}
                        label={t(
                          "terminal-services.fott-service-request.fields.customer-jl-no-size",
                        )}
                        testId="fott-service-request-customer-jl-no"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FormItem<FottServiceRequestFormData, typeof F.CUSTOMER_TANK_NO>
                        required
                        name={F.CUSTOMER_TANK_NO}
                        type="text"
                        maxChars={MAX_PIGGING_TANK_NO_LENGTH}
                        label={t("terminal-services.fott-service-request.fields.customer-tank-no")}
                        testId="fott-service-request-customer-tank-no"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FormItem<FottServiceRequestFormData, typeof F.FOTT_DOCK_LINE>
                        required
                        name={F.FOTT_DOCK_LINE}
                        type="textarea"
                        minRows={1}
                        maxChars={MAX_PIGGING_SHORT_TEXT_LENGTH}
                        label={t(
                          "terminal-services.fott-service-request.fields.fott-dock-line-header-no-size",
                        )}
                        testId="fott-service-request-fott-dock-line"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <FormItem<FottServiceRequestFormData, typeof F.PRODUCT_TYPE>
                        required
                        name={F.PRODUCT_TYPE}
                        type="select"
                        options={piggingProductTypeOptions}
                        label={t("terminal-services.fott-service-request.fields.product-type")}
                        testId="fott-service-request-product-type"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <div className="hidden md:block md:col-span-3" />
                    <div className="md:col-span-full">
                      <FormItem<FottServiceRequestFormData, typeof F.USAGE_REASON>
                        name={F.USAGE_REASON}
                        type="textarea"
                        minRows={1}
                        maxChars={MAX_PIGGING_TEXT_LENGTH}
                        showCharCounter
                        label={t("terminal-services.fott-service-request.fields.usage-reason")}
                        testId="fott-service-request-usage-reason"
                        onFieldChange={updateFormData}
                      />
                    </div>
                  </>
                )}
                {shouldShowBypassFields && (
                  <>
                    <FormItem<FottServiceRequestFormData, typeof F.DATE_TIME>
                      required
                      name={F.DATE_TIME}
                      type="date-picker"
                      combinedInput
                      dateConstraint="not-past"
                      minDate={new Date()}
                      label={t("terminal-services.fott-service-request.fields.date-time")}
                      testId="fott-service-request-date-time"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.LOCATION>
                      required
                      name={F.LOCATION}
                      type="select"
                      options={berthLocationOptions}
                      label={t("terminal-services.fott-service-request.fields.location")}
                      testId="fott-service-request-location"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.VESSEL_IMO>
                      name={F.VESSEL_IMO}
                      type="number"
                      maxDigits={7}
                      label={t("terminal-services.fott-service-request.fields.vessel-imo")}
                      testId="fott-service-request-vessel-imo"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.VESSEL_NAME>
                      name={F.VESSEL_NAME}
                      type="text"
                      label={t("terminal-services.fott-service-request.fields.vessel-name")}
                      testId="fott-service-request-vessel-name"
                      onFieldChange={updateFormData}
                      disabled
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.TERMINAL>
                      required
                      name={F.TERMINAL}
                      type="select"
                      options={companyOptions}
                      label={t("terminal-services.fott-service-request.fields.terminal")}
                      testId="fott-service-request-terminal"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.OPERATION_TYPE>
                      required
                      name={F.OPERATION_TYPE}
                      type="select"
                      options={[
                        { label: "Loading", value: "LOADING" },
                        { label: "Discharge", value: "DISCHARGING" },
                      ]}
                      label={t("terminal-services.fott-service-request.fields.operation-type")}
                      testId="fott-service-request-operation-type"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.DOCK_LINE>
                      required
                      name={F.DOCK_LINE}
                      type="text"
                      maxChars={MAX_BYPASS_SHORT_TEXT_LENGTH}
                      label={t("terminal-services.fott-service-request.fields.dock-line")}
                      testId="fott-service-request-dock-line"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.MOV_NUMBER>
                      required
                      name={F.MOV_NUMBER}
                      type="text"
                      maxChars={MAX_BYPASS_SHORT_TEXT_LENGTH}
                      label={t("terminal-services.fott-service-request.fields.mov-number")}
                      testId="fott-service-request-mov-number"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.PLTA>
                      required
                      name={F.PLTA}
                      type="text"
                      maxChars={MAX_BYPASS_SHORT_TEXT_LENGTH}
                      label={t("terminal-services.fott-service-request.fields.plta")}
                      testId="fott-service-request-plta"
                      onFieldChange={updateFormData}
                    />
                    <FormItem<FottServiceRequestFormData, typeof F.PLDA_NUMBER>
                      required
                      name={F.PLDA_NUMBER}
                      type="text"
                      label={t("terminal-services.fott-service-request.fields.plda-number")}
                      testId="fott-service-request-plda-number"
                      onFieldChange={updateFormData}
                    />
                    <div className="md:col-span-full">
                      <FormItem<FottServiceRequestFormData, typeof F.REASON_FOR_BYPASS>
                        name={F.REASON_FOR_BYPASS}
                        type="textarea"
                        minRows={1}
                        maxChars={MAX_BYPASS_TEXT_LENGTH}
                        showCharCounter
                        label={t("terminal-services.fott-service-request.fields.reason-for-bypass")}
                        testId="fott-service-request-reason-for-bypass"
                        onFieldChange={updateFormData}
                      />
                    </div>
                    <FormItem<FottServiceRequestFormData, typeof F.EXPECTED_TIME_OF_CLOSURE>
                      required
                      name={F.EXPECTED_TIME_OF_CLOSURE}
                      type="date-picker"
                      combinedInput
                      dateConstraint="not-past"
                      minDate={new Date()}
                      label={t(
                        "terminal-services.fott-service-request.fields.expected-time-of-closure",
                      )}
                      testId="fott-service-request-expected-time-of-closure"
                      onFieldChange={updateFormData}
                    />
                  </>
                )}
              </FormSection>

              {shouldShowBypassFields && (
                <FormSection
                  title={t(
                    "terminal-services.fott-service-request.bypass-mitigation-section-title",
                  )}
                >
                  <FormItem<FottServiceRequestFormData, typeof F.PHYSICAL_VERIFICATION_OF_VALVE_TAG>
                    required
                    name={F.PHYSICAL_VERIFICATION_OF_VALVE_TAG}
                    type="radio"
                    horizontalRadioButtons
                    label={t(
                      "terminal-services.fott-service-request.fields.physical-verification-of-valve-tag",
                    )}
                    options={yesNoOptions}
                    testId="fott-service-request-physical-verification-of-valve-tag"
                    onFieldChange={updateFormData}
                  />
                  <FormItem<FottServiceRequestFormData, typeof F.COLLECTION_OF_VALVE_KEY>
                    required
                    name={F.COLLECTION_OF_VALVE_KEY}
                    type="radio"
                    horizontalRadioButtons
                    label={t(
                      "terminal-services.fott-service-request.fields.collection-of-valve-key",
                    )}
                    options={yesNoOptions}
                    testId="fott-service-request-collection-of-valve-key"
                    onFieldChange={updateFormData}
                  />
                  <FormItem<FottServiceRequestFormData, typeof F.REMOVAL_OF_PADLOCK>
                    required
                    name={F.REMOVAL_OF_PADLOCK}
                    type="radio"
                    horizontalRadioButtons
                    label={t("terminal-services.fott-service-request.fields.removal-of-padlock")}
                    options={yesNoOptions}
                    testId="fott-service-request-removal-of-padlock"
                    onFieldChange={updateFormData}
                  />
                  <FormItem<FottServiceRequestFormData, typeof F.CROSS_CHECK_COMMUNICATION>
                    required
                    name={F.CROSS_CHECK_COMMUNICATION}
                    type="radio"
                    horizontalRadioButtons
                    label={t(
                      "terminal-services.fott-service-request.fields.cross-check-communication",
                    )}
                    options={yesNoOptions}
                    testId="fott-service-request-cross-check-communication"
                    onFieldChange={updateFormData}
                  />
                  <FormItem<
                    FottServiceRequestFormData,
                    typeof F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER
                  >
                    required
                    name={F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER}
                    type="radio"
                    horizontalRadioButtons
                    label={t(
                      "terminal-services.fott-service-request.fields.confirmation-of-instructions-customer",
                    )}
                    options={yesNoOptions}
                    testId="fott-service-request-confirmation-of-instructions-customer"
                    onFieldChange={updateFormData}
                  />
                  <FormItem<FottServiceRequestFormData, typeof F.CONFIRMATION_OF_INSTRUCTIONS_FOTT>
                    required
                    name={F.CONFIRMATION_OF_INSTRUCTIONS_FOTT}
                    type="radio"
                    horizontalRadioButtons
                    label={t(
                      "terminal-services.fott-service-request.fields.confirmation-of-instructions-fott",
                    )}
                    options={yesNoOptions}
                    testId="fott-service-request-confirmation-of-instructions-fott"
                    onFieldChange={updateFormData}
                  />
                </FormSection>
              )}

              {!shouldShowGantryCraneFields && shouldShowAttachmentSection && renderRemarksField()}

              {shouldShowAttachmentSection && (
                <FormSection
                  isFullWidth
                  title={t(
                    shouldShowGantryCraneFields
                      ? "terminal-services.fott-service-request.operator-license-title"
                      : "terminal-services.fott-service-request.additional-documents-title",
                  )}
                >
                  {shouldShowGantryCraneFields && (
                    <>
                      <FormItem<FottServiceRequestFormData, typeof F.OPERATOR_LICENSE>
                        name={F.OPERATOR_LICENSE}
                        type="file"
                        maxFiles={MAX_OPERATOR_LICENSE_FILES}
                        label={t("terminal-services.fott-service-request.operator-license-title")}
                        testId="fott-service-request-operator-license"
                        uploadConditions={uploadConditions}
                        allowedFileExtensions={allowedOperatorLicenseExtensions}
                        uploadDescription={t("upload.additional-documents-upload", {
                          extensions: uploadConditions.format?.join(", "),
                          size: uploadConditions.maxSizeLabel,
                        })}
                        files={operatorLicenseFiles}
                        setFiles={setOperatorLicenseFiles}
                        onUpload={(_, files) => handleOperatorLicenseUpload(files)}
                        onRemoveFile={removeOperatorLicense}
                        disabled={isSubmitting || isUploadingOperatorLicense}
                      />

                      {operatorLicenseFiles.length > 0 && (
                        <SingleFile
                          testId="fott-service-request-operator-license-file"
                          file={operatorLicenseFiles[0]}
                          fieldName={F.OPERATOR_LICENSE}
                          currentFileIndex={0}
                          setFiles={removeOperatorLicense}
                          removeFn={removeOperatorLicense}
                          isLoading={
                            isUploadingOperatorLicense && operatorLicenseFiles[0] instanceof File
                          }
                        />
                      )}
                    </>
                  )}

                  {shouldShowNitrogenSupplyFields && (
                    <TerminalServiceDocumentsCard
                      files={attachmentFiles}
                      setFiles={setAttachmentFiles}
                      onUpload={handleAttachmentUpload}
                      onAttachmentsChange={(attachments) =>
                        updateFormData(F.ATTACHMENT, attachments)
                      }
                      maxFiles={MAX_NITROGEN_ATTACHMENTS}
                      uploadConditions={uploadConditions}
                      allowedFileExtensions={allowedOperatorLicenseExtensions}
                      disabled={isSubmitting}
                      isUploading={isUploadingAttachment}
                    />
                  )}

                  {shouldShowPiggingFields && (
                    <TerminalServiceDocumentsCard
                      files={piggingAttachmentFiles}
                      setFiles={setPiggingAttachmentFiles}
                      onUpload={handlePiggingAttachmentUpload}
                      onAttachmentsChange={(attachments) =>
                        updateFormData(F.ATTACHMENT, attachments)
                      }
                      maxFiles={MAX_PIGGING_ATTACHMENTS}
                      uploadConditions={uploadConditions}
                      allowedFileExtensions={allowedOperatorLicenseExtensions}
                      disabled={isSubmitting}
                      isUploading={isUploadingPiggingAttachment}
                    />
                  )}

                  {shouldShowBypassFields && (
                    <TerminalServiceDocumentsCard
                      files={bypassAttachmentFiles}
                      setFiles={setBypassAttachmentFiles}
                      onUpload={handleBypassAttachmentUpload}
                      onAttachmentsChange={(attachments) =>
                        updateFormData(F.ATTACHMENT, attachments)
                      }
                      uploadConditions={uploadConditions}
                      allowedFileExtensions={allowedOperatorLicenseExtensions}
                      disabled={isSubmitting}
                      isUploading={isUploadingBypassAttachment}
                    />
                  )}
                </FormSection>
              )}

              {shouldShowGantryCraneFields && (
                <>
                  {renderRemarksField()}

                  <FormSection
                    isFullWidth
                    title={t("terminal-services.fott-service-request.additional-documents-title")}
                  >
                    <TerminalServiceDocumentsCard
                      files={attachmentFiles}
                      setFiles={setAttachmentFiles}
                      onUpload={handleAttachmentUpload}
                      onAttachmentsChange={(attachments) =>
                        updateFormData(F.ATTACHMENT, attachments)
                      }
                      maxFiles={MAX_GANTRY_ATTACHMENTS}
                      uploadConditions={uploadConditions}
                      allowedFileExtensions={allowedOperatorLicenseExtensions}
                      disabled={isSubmitting}
                      isUploading={isUploadingAttachment}
                    />
                  </FormSection>
                </>
              )}

              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  testId="fott-service-request-cancel-button"
                  label={t("common.cancel")}
                  sx={{ textTransform: "none", px: 0 }}
                  onClick={handleCancel}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || isUploading}
                  testId="fott-service-request-submit-button"
                  label={
                    <span className="text-sm font-medium flex items-center gap-2">
                      <ContentPasteGoIcon fontSize="small" />
                      {t("common.submit")}
                    </span>
                  }
                  sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </PageLayout>
  );
};

export default FottServiceRequestContainer;
